import { useEffect, useState } from "react";
import { Panel } from "./Panels";
import { RolesManager } from "./RolesManager";
import { ArmedActionsManager } from "./ArmedActionsManager";
import { api } from "../api";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import type { DashboardData } from "./useDashboardData";
import type {
  CommandRun,
  CommandToolRun,
  CommandTimelineEvent,
  CommandAgent,
  CommandBackupRestoreSignal,
  CommandHostHealthSignal,
  CommandInfraProbe,
  CommandTailnetHost,
  LearningProposalView,
  NotificationView,
  NotificationReplyAction,
} from "../api";

// ── Status → Akzent/Label-Helfer ───────────────────────────────────────
const RUN_ACCENT: Record<string, string> = {
  running: "simo-accent-acting",
  awaiting_approval: "simo-accent-confirming",
  done: "simo-accent-ok",
  error: "simo-accent-error",
  cancelled: "simo-accent-neutral",
};
const RUN_LABEL: Record<string, string> = {
  running: "läuft", awaiting_approval: "wartet auf Freigabe", done: "fertig", error: "Fehler", cancelled: "abgebrochen",
};
const PROBE_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", degraded: "simo-accent-warn", down: "simo-accent-down", not_configured: "simo-accent-neutral",
};
const PROBE_LABEL: Record<string, string> = {
  ok: "online", degraded: "degradiert", down: "offline", not_configured: "nicht konfiguriert",
};
const FORGEJO_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", auth_required: "simo-accent-warn", degraded: "simo-accent-warn", down: "simo-accent-down",
};
const FORGEJO_LABEL: Record<string, string> = {
  ok: "primary", auth_required: "Auth fehlt", degraded: "Drift", down: "offline",
};
const SIMO_CI_ACCENT: Record<string, string> = {
  up: "simo-accent-ok", warn: "simo-accent-warn", down: "simo-accent-down",
};
const SIMO_CI_LABEL: Record<string, string> = {
  up: "CI grün", warn: "CI offen", down: "CI rot",
};
const KUMA_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", degraded: "simo-accent-warn", down: "simo-accent-down",
};
const KUMA_LABEL: Record<string, string> = {
  ok: "online", degraded: "Warnung", down: "offline",
};
const BACKUP_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", conditional: "simo-accent-warn", down: "simo-accent-down",
};
const BACKUP_LABEL: Record<string, string> = {
  ok: "bereit", conditional: "conditional", down: "offline",
};
const RESTORE_REHEARSAL_ACCENT: Record<string, string> = {
  fresh: "simo-accent-ok", stale: "simo-accent-warn", missing: "simo-accent-down",
};
const RESTORE_REHEARSAL_LABEL: Record<string, string> = {
  fresh: "frisch", stale: "Drill fällig", missing: "Proof fehlt",
};
const HOST_HEALTH_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", degraded: "simo-accent-warn", down: "simo-accent-down",
};
const HOST_HEALTH_LABEL: Record<string, string> = {
  ok: "gesund", degraded: "Warnung", down: "offline",
};
const RELIABILITY_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", degraded: "simo-accent-warn", down: "simo-accent-down", not_measured: "simo-accent-neutral",
};
const RELIABILITY_LABEL: Record<string, string> = {
  ok: "ok", degraded: "Warnung", down: "down", not_measured: "nicht gemessen",
};
const AGENT_ACCENT: Record<string, string> = {
  idle: "simo-accent-neutral", active: "simo-accent-acting", blocked: "simo-accent-confirming", error: "simo-accent-error",
};
const TYPE_GLYPH: Record<string, string> = { run: "▶", tool: "⚙", approval: "✋", audit: "▣", evidence: "§" };
const TOOL_STATUS_ACCENT: Record<string, string> = {
  ok: "simo-accent-ok", error: "simo-accent-error", blocked: "simo-accent-confirming", pending: "simo-accent-acting",
};
const RISK_LABEL: Record<string, string> = {
  read: "read", draft: "draft", write: "write", destructive: "destruktiv",
};
const MEM_EMB_ACCENT: Record<string, string> = {
  current: "simo-accent-ok", stale: "simo-accent-warn", missing: "simo-accent-neutral",
};
const MEM_EMB_LABEL: Record<string, string> = {
  current: "aktuell", stale: "veraltet", missing: "fehlt",
};
const LEARNING_KIND_LABEL: Record<string, string> = {
  memory: "Memory", skill: "Skill", policy: "Policy", project_truth: "Project Truth",
};
const NOTIFICATION_ACCENT: Record<string, string> = {
  high: "simo-accent-confirming", normal: "simo-accent-acting", low: "simo-accent-neutral",
};
const ALERT_LANE_ACCENT: Record<string, string> = {
  owner_escalation: "simo-accent-confirming", priority: "simo-accent-warn", routine: "simo-accent-neutral", muted: "simo-accent-neutral",
};
const ALERT_LANE_LABEL: Record<string, string> = {
  owner_escalation: "Owner", priority: "Priorität", routine: "Routine", muted: "Snooze",
};

function shortTime(iso: string): string {
  // HH:MM ohne Sekunden — kompakt für Timeline/Run-Listen.
  const t = iso.slice(11, 16);
  return t || iso.slice(0, 10);
}

function commandPanelErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function commandPanelActionErrorCopy(error: unknown, surface: "learning" | "notification"): string {
  const text = commandPanelErrorText(error);
  if (/work_queue_item_rejected_by_secret_scan|secret/i.test(text)) {
    return "Idee wurde nicht in die Work Queue gelegt, weil sie wie ein Secret wirkt. Bitte ohne Zugangsdaten erneut erfassen.";
  }
  if (/invalid_work_queue_item|work_queue_intake_forbidden|work queue/i.test(text)) {
    return "Idee konnte nicht in die Work Queue gelegt werden. Bitte Inhalt und Owner-Session pruefen.";
  }
  if (/proposal.*not found|learning_proposal.*not found|proposal.*missing|not_pending|already.*decided|already.*applied|already.*rejected|invalid_state|proposal_state/i.test(text)) {
    return "Learning-Vorschlag ist nicht mehr aktuell. Bitte Inbox neu laden und Entscheidung erneut pruefen.";
  }
  if (/notification.*not found|notification.*missing|already.*processed|already.*replied|idempotency|correlation.*missing|notification_state/i.test(text)) {
    return "Benachrichtigung ist nicht mehr aktuell. Bitte Inbox neu laden und Aktion erneut pruefen.";
  }
  const fallback = ownerFacingGlobalError(text);
  if (surface === "learning" && fallback === text && /memory.*review|retention|injectPolicy|sensitivity|space/i.test(text)) {
    return "Learning-Vorschlag konnte nicht uebernommen werden. Bitte Memory-Felder pruefen und erneut entscheiden.";
  }
  return fallback;
}

// ── KPI-Übersicht ───────────────────────────────────────────────────────
export function OverviewPanel({ data }: { data: DashboardData }): JSX.Element {
  const o = data.overview;
  const tiles: Array<{ label: string; value: string; accent: string }> = [
    { label: "Runs aktiv", value: data.controlPlaneSummary?.semantics ? String(data.controlPlaneSummary.semantics.runs.nonTerminal) : "UNAVAILABLE", accent: (data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? 0) > 0 ? "simo-accent-acting" : "simo-accent-neutral" },
    { label: "Runs gesamt", value: o ? String(o.runs.total) : "—", accent: "simo-accent-idle" },
    { label: "Approvals", value: o ? String(o.approvals.pending) : "—", accent: o && o.approvals.pending > 0 ? "simo-accent-confirming" : "simo-accent-neutral" },
    { label: "Tool-Runs", value: o ? String(o.tools.total) : "—", accent: "simo-accent-idle" },
    { label: "Connectoren", value: o ? `${o.connectors.reachable}/${o.connectors.total}` : "—", accent: "simo-accent-ok" },
    { label: "Memory", value: o ? String(o.memory.count) : "—", accent: "simo-accent-idle" },
  ];
  return (
    <Panel title="Übersicht" accent="simo-accent-idle">
      <div className="grid grid-cols-3 gap-2">
        {tiles.map((t) => (
          <div key={t.label} className={`rounded-md border border-jarvis-border/60 bg-jarvis-bg/40 p-2 ${t.accent}`}>
            <div className="kpi text-lg leading-none">{t.value}</div>
            <div className="text-[9px] uppercase tracking-wider text-jarvis-muted mt-1 flex items-center gap-1">
              <span className="status-dot" style={{ width: 5, height: 5 }} />
              <span className="truncate">{t.label}</span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ── Letzte AgentGraph-Runs ──────────────────────────────────────────────
export function RunsPanel({ data, focused }: { data: DashboardData; focused?: boolean }): JSX.Element {
  const runs: CommandRun[] = data.runs;
  return (
    <Panel
      title="AgentGraph Runs"
      accent={runs.some((r) => r.status === "running") ? "simo-accent-acting" : "simo-accent-neutral"}
      focused={focused}
      action={<span className="kpi text-xs text-jarvis-muted">{data.overview?.runs.total ?? runs.length}</span>}
    >
      {runs.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Noch keine Runs.</div>
      ) : (
        <ul className="space-y-1">
          {runs.slice(0, 8).map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${RUN_ACCENT[r.status] ?? "simo-accent-neutral"}`} />
              <span className="truncate flex-1 min-w-0" title={r.request}>{r.request || r.assistantMode || r.id}</span>
              <span className="text-[10px] text-jarvis-muted shrink-0">{RUN_LABEL[r.status] ?? r.status}</span>
              <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{shortTime(r.updatedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── Command-Timeline (gemergte Read-only-Events) ────────────────────────
export function TimelinePanel({ data }: { data: DashboardData }): JSX.Element {
  const ev = data.timeline;
  const metaFor = (e: CommandTimelineEvent): Array<[string, string]> => [
    ["Request", e.requestId],
    ["Agent", e.agentId],
    ["Evidence", e.evidenceRef],
    ["Approval", e.approvalId],
    ["Audit", e.auditId == null ? null : String(e.auditId)],
    ["Action", e.actionId],
    ["Capability", e.capability],
  ].filter((item): item is [string, string] => typeof item[1] === "string" && item[1].length > 0);

  return (
    <Panel title="Command Timeline" accent="simo-accent-idle" action={<span className="kpi text-xs text-jarvis-muted">{ev.length}</span>}>
      {ev.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Noch keine Aktivität.</div>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto simo-thin-scroll pr-1">
          {ev.slice(0, 40).map((e) => (
            <li key={e.id} className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/25 px-1.5 py-1 text-xs">
              <div className="flex items-baseline gap-2">
                <span className="shrink-0 text-jarvis-muted/70 w-4 text-center">{TYPE_GLYPH[e.type] ?? "•"}</span>
                <span className="truncate flex-1 min-w-0" title={e.summary}>{e.summary}</span>
                {e.status && <span className="text-[9px] uppercase tracking-wider text-jarvis-muted shrink-0">{e.status}</span>}
                <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{shortTime(e.at)}</span>
              </div>
              {metaFor(e).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 pl-6">
                  {metaFor(e).slice(0, 5).map(([label, value]) => (
                    <span key={`${label}:${value}`} className="badge bg-jarvis-bg/60 text-[9px] text-jarvis-muted max-w-full">
                      {label}: <span className="font-mono truncate">{value}</span>
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── Tool-Runs (persistiert, read-only live) ─────────────────────────────
// SIMO-DASHBOARD-PANELS-LIVE-2: echte tool_runs aus der DB statt der
// Session-Ableitung (ToolsPanel). Nur Metadaten — keine Tool-Outputs.
export function LiveToolRunsPanel({ data }: { data: DashboardData }): JSX.Element {
  const tools: CommandToolRun[] = data.toolRuns;
  const running = tools.some((t) => t.status === "pending");
  return (
    <Panel
      title="Tool-Runs"
      accent={running ? "simo-accent-acting" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{data.overview?.tools.total ?? tools.length}</span>}
    >
      {tools.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Noch keine Tool-Calls.</div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto simo-thin-scroll pr-1">
          {tools.slice(0, 12).map((t) => (
            <li key={t.id} className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${TOOL_STATUS_ACCENT[t.status] ?? "simo-accent-neutral"}`} />
              <span className="truncate flex-1 min-w-0 font-mono text-[11px]" title={t.toolId}>{t.toolId}</span>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">{RISK_LABEL[t.riskLevel] ?? t.riskLevel}</span>
              {t.durationMs != null && (
                <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{t.durationMs} ms</span>
              )}
              <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{shortTime(t.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── Memory (read-only live, redigiert: nur Keys + Metadaten) ─────────────
// SIMO-DASHBOARD-PANELS-LIVE-2: zeigt bewusst NUR Key + Embedding-Status +
// Zeit. Rohe Memory-Values (potenziell Secrets/PII) werden nicht angezeigt —
// sie liegen gar nicht erst im Dashboard-State (siehe useDashboardData).
export function MemoryLivePanel({ data }: { data: DashboardData }): JSX.Element {
  const entries = data.memory;
  const stale = entries.filter((e) => e.embeddingStatus !== "current").length;
  return (
    <Panel
      title="Memory"
      accent={stale > 0 ? "simo-accent-warn" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{data.memoryTotal}</span>}
    >
      {entries.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Noch keine Erinnerungen.</div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto simo-thin-scroll pr-1">
          {entries.slice(0, 12).map((e) => (
            <li key={e.key} className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${MEM_EMB_ACCENT[e.embeddingStatus] ?? "simo-accent-neutral"}`} />
              <span className="truncate flex-1 min-w-0 font-mono text-[11px]" title={e.key}>{e.key}</span>
              <span className="text-[9px] uppercase tracking-wider text-jarvis-muted shrink-0">{MEM_EMB_LABEL[e.embeddingStatus] ?? e.embeddingStatus}</span>
              <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{shortTime(e.updatedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── Governed Learning: Vorschläge statt Auto-Mutation ───────────────────
// Zeigt pending Learning-Proposals und ruft nur den Governance-Endpunkt auf.
// Skill/Policy/Project-Truth bleiben serverseitig reviewbare Entscheidungen;
// nur Memory-Proposals werden beim Apply vom Server in Memory geschrieben.
export function LearningProposalsPanel({ data, onOpenAgentOs }: { data: DashboardData; onOpenAgentOs?: () => void }): JSX.Element {
  const proposals: LearningProposalView[] = data.learningProposals;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const moveToWorkQueue = async (proposal: LearningProposalView): Promise<void> => {
    setBusyId(proposal.id);
    setError(null);
    try {
      const title = proposal.targetKey.replace(/[_./:-]+/g, " ").trim().slice(0, 120) || proposal.proposedValue.slice(0, 80);
      await api.createWorkQueueIdea({
        title,
        goal: proposal.proposedValue,
        productScope: "SIMO",
        assignedAgent: "simo",
        priority: "normal",
      });
      try {
        await api.decideLearningProposal(proposal.id, "reject");
      } catch {
        setError("Aufgabe wurde in die Work Queue gelegt, aber der Learning-Vorschlag konnte noch nicht entfernt werden. Bitte Review Inbox aktualisieren und den Vorschlag danach ablehnen.");
        data.refresh();
        return;
      }
      data.refresh();
    } catch (err) {
      setError(commandPanelActionErrorCopy(err, "learning"));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: string): Promise<void> => {
    setBusyId(id);
    setError(null);
    try {
      await api.decideLearningProposal(id, "reject");
      data.refresh();
    } catch (err) {
      setError(commandPanelActionErrorCopy(err, "learning"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      title="Memory Review Inbox"
      accent={proposals.length > 0 ? "simo-accent-confirming" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{proposals.length}</span>}
    >
      {proposals.length === 0 ? (
        <div className="space-y-1 text-[11px] text-jarvis-muted">
          <div>Keine offenen Vorschläge.</div>
          <div className="text-[10px] text-jarvis-muted/70">Neue Memories bleiben Vorschläge, bis Space, Sensitivity, Retention und Injection freigegeben sind.</div>
        </div>
      ) : (
        <ul className="simo-keyboard-safe-editor space-y-2 max-h-72 overflow-y-auto simo-thin-scroll pr-1">
          {proposals.slice(0, 10).map((p) => (
              <li key={p.id} className="simo-keyboard-safe-editor rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-2">
                <div className="flex items-center gap-2">
                  <span className="status-dot simo-accent-confirming" />
                  <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">{LEARNING_KIND_LABEL[p.kind] ?? p.kind}</span>
                  <span className="truncate flex-1 min-w-0 font-mono text-[11px]" title={p.targetKey}>{p.targetKey}</span>
                </div>
                <div className="mt-1 text-xs text-jarvis-text line-clamp-2">{p.proposedValue}</div>
                <div className="mt-1 text-[10px] text-jarvis-muted line-clamp-2">{p.reason}</div>
                <p className="mt-2 rounded border border-amber-300/25 bg-amber-300/[0.06] px-2 py-1.5 text-[10px] text-amber-100">Apply ist hier gesperrt. Diff, Metadaten, Owner-Freigabe, Apply, Audit und Rollback laufen ausschließlich im Agent OS Studio.</p>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(7.5rem,auto)_minmax(7.5rem,auto)_minmax(12rem,1fr)_auto] xl:items-center">
                  <button
                    type="button"
                    disabled={!onOpenAgentOs || busyId === p.id}
                    onClick={onOpenAgentOs}
                    className="min-h-10 rounded border border-emerald-400/40 w-full px-2 py-2 text-[10px] uppercase leading-tight tracking-wider text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-50"
                  >
                    Im Agent OS Studio prüfen
                  </button>
                  <button
                    type="button"
                    disabled={busyId === p.id}
                    onClick={() => void reject(p.id)}
                    className="min-h-10 rounded border border-jarvis-border w-full px-2 py-2 text-[10px] uppercase leading-tight tracking-wider text-jarvis-muted hover:bg-jarvis-bg/70 disabled:opacity-50"
                  >
                    Ablehnen
                  </button>
                  <button
                    type="button"
                    disabled={busyId === p.id}
                    onClick={() => void moveToWorkQueue(p)}
                    className="min-h-10 rounded border border-sky-300/40 w-full px-2 py-2 text-[10px] uppercase leading-tight tracking-wider text-sky-100 hover:bg-sky-300/10 disabled:opacity-50 sm:col-span-2 xl:col-span-1"
                  >
                    In Work Queue legen
                  </button>
                  <span className="text-right text-[10px] text-jarvis-muted/70 sm:col-span-2 sm:text-left xl:col-span-1 xl:text-right">{shortTime(p.createdAt)}</span>
                </div>
              </li>
          ))}
        </ul>
      )}
      {error && <div className="mt-2 text-[10px] text-red-200">{error}</div>}
    </Panel>
  );
}

export function NotificationInboxPanel({ data }: { data: DashboardData }): JSX.Element {
  const centerById = new Map(data.notificationCenterItems.map((item) => [item.id, item]));
  const notifications: NotificationView[] = [...data.notifications]
    .filter((n) => n.status !== "archived")
    .sort((left, right) => {
      const leftPriority = centerById.get(left.id)?.priority ?? 0;
      const rightPriority = centerById.get(right.id)?.priority ?? 0;
      if (rightPriority !== leftPriority) return rightPriority - leftPriority;
      return right.createdAt.localeCompare(left.createdAt);
    });
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const actionable = (n: NotificationView): boolean =>
    n.status === "unread" && n.sourceKind === "learning_proposal";
  const qualityCounts = data.notificationCenterItems.reduce(
    (counts, item) => ({ ...counts, [item.alertLane]: (counts[item.alertLane] ?? 0) + 1 }),
    {} as Record<string, number>,
  );

  const reply = async (n: NotificationView, action: Extract<NotificationReplyAction, "reject">): Promise<void> => {
    const key = `${n.id}:${action}`;
    setBusyKey(key);
    setError(null);
    try {
      await api.replyNotification({
        correlationId: n.correlationId,
        channel: "web",
        action,
        idempotencyKey: `web:${n.correlationId}:${action}`,
      });
      data.refresh();
    } catch (err) {
      setError(commandPanelActionErrorCopy(err, "notification"));
    } finally {
      setBusyKey(null);
    }
  };
  const mark = async (n: NotificationView, status: "read" | "archived"): Promise<void> => {
    const key = `${n.id}:${status}`;
    setBusyKey(key);
    setError(null);
    try {
      await api.markNotification(n.id, status);
      data.refresh();
    } catch (err) {
      setError(commandPanelActionErrorCopy(err, "notification"));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Panel
      title="Notification Inbox"
      accent={notifications.some((n) => n.status === "unread") ? "simo-accent-acting" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{notifications.filter((n) => n.status === "unread").length}</span>}
    >
      {notifications.length === 0 ? (
        <div className="space-y-1 text-[11px] text-jarvis-muted">
          <div>Keine offenen Inbox-Events.</div>
          <div className="text-[10px] text-jarvis-muted/70">Web, Mobile und Teams teilen denselben Reply-Pfad.</div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1 text-[10px] text-jarvis-muted">
            <span className="badge bg-jarvis-bg/60">Owner {qualityCounts.owner_escalation ?? 0}</span>
            <span className="badge bg-jarvis-bg/60">Priorität {qualityCounts.priority ?? 0}</span>
            <span className="badge bg-jarvis-bg/60">Routine {qualityCounts.routine ?? 0}</span>
            <span className="badge bg-jarvis-bg/60">Snooze {qualityCounts.muted ?? 0}</span>
          </div>
          <ul className="space-y-2 max-h-72 overflow-y-auto simo-thin-scroll pr-1">
          {notifications.slice(0, 10).map((n) => (
            <li key={n.id} className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-2">
              <div className="flex items-center gap-2">
                <span className={`status-dot ${NOTIFICATION_ACCENT[n.urgency] ?? "simo-accent-neutral"}`} />
                <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">{n.type}</span>
                <span className="truncate flex-1 min-w-0 text-xs text-jarvis-text" title={n.title}>{n.title}</span>
                {centerById.get(n.id) && (
                  <span className={`badge bg-jarvis-bg/60 text-jarvis-muted shrink-0 ${ALERT_LANE_ACCENT[centerById.get(n.id)!.alertLane] ?? ""}`}>
                    {ALERT_LANE_LABEL[centerById.get(n.id)!.alertLane] ?? centerById.get(n.id)!.alertLane}
                  </span>
                )}
                <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{shortTime(n.createdAt)}</span>
              </div>
              <div className="mt-1 text-[11px] text-jarvis-muted line-clamp-2">{n.body}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {n.deliveries.length === 0 ? (
                  <span className="badge bg-jarvis-bg/60 text-jarvis-muted">web pending</span>
                ) : n.deliveries.map((d) => (
                  <span key={d.id} className="badge bg-jarvis-bg/60 text-jarvis-muted">{d.provider}:{d.status}</span>
                ))}
              </div>
              {actionable(n) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="badge bg-jarvis-bg/60 text-amber-200">Apply nur im Agent OS Studio</span>
                  <button
                    type="button"
                    disabled={busyKey === `${n.id}:reject`}
                    onClick={() => void reply(n, "reject")}
                    className="rounded border border-jarvis-border px-2 py-1 text-[10px] uppercase tracking-wider text-jarvis-muted hover:bg-jarvis-bg/70 disabled:opacity-50"
                  >
                    Ablehnen
                  </button>
                </div>
              )}
              {n.status === "unread" && (
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    disabled={busyKey === `${n.id}:read`}
                    onClick={() => void mark(n, "read")}
                    className="rounded border border-jarvis-border px-2 py-1 text-[10px] uppercase tracking-wider text-jarvis-muted hover:bg-jarvis-bg/70 disabled:opacity-50"
                  >
                    Quittieren
                  </button>
                  {!centerById.get(n.id)?.ownerOnlyEscalation && (
                    <button
                      type="button"
                      disabled={busyKey === `${n.id}:archived`}
                      onClick={() => void mark(n, "archived")}
                      className="rounded border border-jarvis-border px-2 py-1 text-[10px] uppercase tracking-wider text-jarvis-muted hover:bg-jarvis-bg/70 disabled:opacity-50"
                    >
                      Snooze
                    </button>
                  )}
                </div>
              )}
              {n.sourceKind === "approval" && (
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  <span className="badge bg-jarvis-bg/60 text-amber-200">Legacy-Aktionspfad blockiert</span>
                  <span className="text-[10px] text-jarvis-muted">Approval Inbox ist read/action-gated.</span>
                </div>
              )}
            </li>
          ))}
          </ul>
        </div>
      )}
      {error && <div className="mt-2 text-[10px] text-red-200">{error}</div>}
    </Panel>
  );
}

// ── Agenten-Roster (abgeleitet, read-only) ──────────────────────────────
export function AgentRosterPanel({ data }: { data: DashboardData }): JSX.Element {
  const agents: CommandAgent[] = data.agents;
  const [manageOpen, setManageOpen] = useState(false);
  return (
    <Panel
      title="Agenten"
      accent={agents.some((a) => a.liveStatus === "active") ? "simo-accent-acting" : "simo-accent-neutral"}
      action={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setManageOpen(true)}
            className="text-[10px] uppercase tracking-wider text-jarvis-accent hover:underline"
          >Verwalten</button>
          <span className="kpi text-xs text-jarvis-muted">{agents.length}</span>
        </div>
      }
    >
      {agents.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Roster wird geladen…</div>
      ) : (
        <ul className="space-y-2">
          {agents.map((a) => (
            <li
              key={a.id}
              className={`rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1.5 ${a.enabled ? "" : "opacity-50"}`}
            >
              <div className="flex items-center gap-2">
                <span className={`status-dot ${AGENT_ACCENT[a.liveStatus] ?? "simo-accent-neutral"}`} />
                <span className="text-xs font-medium text-jarvis-text truncate flex-1 min-w-0">{a.name}</span>
                {!a.enabled && <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">aus</span>}
                {a.permissionFloor !== "none" && (
                  <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">floor:{a.permissionFloor}</span>
                )}
                <span className="badge bg-jarvis-bg/60 text-jarvis-muted shrink-0">{a.accessLevel}</span>
              </div>
              <div className="text-[10px] text-jarvis-muted mt-0.5 line-clamp-2">{a.purpose}</div>
              <div className="text-[10px] text-jarvis-muted/70 mt-0.5">
                {a.tools.length} Tools · Risk {a.highestRiskLevel}{a.isBuiltin ? "" : " · custom"}
              </div>
            </li>
          ))}
        </ul>
      )}
      <RolesManager open={manageOpen} onClose={() => setManageOpen(false)} onChanged={data.refresh} />
    </Panel>
  );
}

// ── Armed-Gate: scharfgeschaltete Aktionen (SIMO-GATED-ACTIONS-5) ────────
export function ArmedActionsPanel({ data }: { data: DashboardData }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const reload = (): void => {
    void api.listArmed()
      .then((r) => { setTotal(r.actions.length); setArmed(r.actions.filter((a) => a.armedState === "armed").length); })
      .catch(() => {});
  };
  useEffect(() => { reload(); }, []);
  return (
    <Panel
      title="Scharfgeschaltete Aktionen"
      accent={armed != null && armed > 0 ? "simo-accent-ok" : "simo-accent-neutral"}
      action={
        <button onClick={() => setOpen(true)} className="text-[10px] uppercase tracking-wider text-jarvis-accent hover:underline">
          Verwalten
        </button>
      }
    >
      <div className="text-xs text-jarvis-muted">
        {armed == null ? "lädt…" : (
          <>
            <span className="kpi text-base text-jarvis-text">{armed}</span>
            <span> / {total} scharf</span>
            {armed === 0 && <div className="text-[10px] text-jarvis-muted/70 mt-1">Fail-secure: nichts scharf — Aktionen werden hart blockiert, bis du sie freigibst.</div>}
          </>
        )}
      </div>
      <ArmedActionsManager open={open} onClose={() => setOpen(false)} onChanged={() => { reload(); data.refresh(); }} />
    </Panel>
  );
}

// ── Infra: Orchestrator/VPS · n8n · GitHub · Tailnet ────────────────────
function ProbeRow({ p }: { p: CommandInfraProbe }): JSX.Element {
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className={`status-dot ${PROBE_ACCENT[p.status] ?? "simo-accent-neutral"}`} />
      <span className="truncate flex-1 min-w-0">{p.name}</span>
      {p.latencyMs != null && p.status === "ok" && (
        <span className="font-mono text-[10px] text-jarvis-muted/70 shrink-0">{p.latencyMs} ms</span>
      )}
      <span className="text-[10px] text-jarvis-muted shrink-0">{PROBE_LABEL[p.status] ?? p.status}</span>
    </li>
  );
}

function TailnetHostRow({ host }: { host: CommandTailnetHost }): JSX.Element {
  const networkLabel = host.privateIp ?? "dediziert";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 text-[11px]">
      <span className="min-w-0">
        <span className="block truncate text-jarvis-text">{host.hostname}</span>
        <span className="block truncate text-[10px] text-jarvis-muted">{host.tailnetName} · {host.role}</span>
      </span>
      <span className="text-right font-mono text-[10px] text-jarvis-muted shrink-0">
        <span className="block">{host.tailscaleIp}</span>
        <span className="block">{networkLabel}</span>
      </span>
    </li>
  );
}

function BackupRestoreRow({ signal }: { signal: CommandBackupRestoreSignal }): JSX.Element {
  const activeTimers = [signal.localBackupTimer, signal.backupMonitorTimer, signal.offsiteBackupTimer]
    .filter((status) => status === "active").length;
  const rehearsal = signal.restoreRehearsal;
  const ageDays = rehearsal.proofAgeHours == null ? null : Math.floor(rehearsal.proofAgeHours / 24);
  return (
    <li className="space-y-1 text-xs">
      <div className="flex items-center gap-2">
      <span className={`status-dot ${BACKUP_ACCENT[signal.status] ?? "simo-accent-neutral"}`} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{signal.name}</span>
        <span className="block truncate text-[10px] text-jarvis-muted">
          {activeTimers}/3 Timer aktiv · Restore Testpfad
        </span>
      </span>
      <span className="text-[10px] text-jarvis-muted shrink-0">{BACKUP_LABEL[signal.status] ?? signal.status}</span>
      </div>
      <div className="ml-4 rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1">
        <div className="flex items-center gap-2">
          <span className={`status-dot ${RESTORE_REHEARSAL_ACCENT[rehearsal.status] ?? "simo-accent-neutral"}`} />
          <span className="min-w-0 flex-1 truncate text-[11px] text-jarvis-text">Restore Drill</span>
          <span className="shrink-0 text-[10px] text-jarvis-muted">
            {RESTORE_REHEARSAL_LABEL[rehearsal.status] ?? rehearsal.status}
          </span>
        </div>
        <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] gap-2 text-[10px] text-jarvis-muted">
          <span className="truncate">
            {ageDays == null ? "kein Proof" : `letzter Proof vor ${ageDays} Tagen`}
          </span>
          <span className="shrink-0">
            {rehearsal.noLiveRestoreAction ? "read-only" : "gated"}
          </span>
        </div>
      </div>
    </li>
  );
}

function HostHealthRow({ signal }: { signal: CommandHostHealthSignal }): JSX.Element {
  const detail = signal.down > 0
    ? `${signal.down} down · ${signal.ok}/${signal.total} ok`
    : signal.degraded > 0
      ? `${signal.degraded} warn · ${signal.ok}/${signal.total} ok`
      : `${signal.ok}/${signal.total} Hosts ok`;
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className={`status-dot ${HOST_HEALTH_ACCENT[signal.status] ?? "simo-accent-neutral"}`} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{signal.name}</span>
        <span className="block truncate text-[10px] text-jarvis-muted">{detail}</span>
      </span>
      <span className="text-[10px] text-jarvis-muted shrink-0">{HOST_HEALTH_LABEL[signal.status] ?? signal.status}</span>
    </li>
  );
}

export type VersioningControlTone = "ready" | "gated" | "blocked";

export interface VersioningControlRow {
  id: string;
  label: string;
  status: string;
  detail: string;
  tone: VersioningControlTone;
}

export interface VersioningClosureRequestRow {
  id: string;
  label: string;
  request: string;
  evidence: string;
  gate: string;
  tone: VersioningControlTone;
}

export type VersioningProposalAction = "prepare_pr" | "request_merge" | "request_safe_tag" | "check_mirror_drift" | "release_readiness";

export interface VersioningLearningProposalDraft {
  kind: "policy";
  targetKey: string;
  proposedValue: string;
  source: "forge-versioning-control";
  reason: string;
  evidence: {
    schema: "it-simo.forge-versioning-proposal.v1";
    action: VersioningProposalAction;
    note: string;
    readyRows: number;
    gatedRows: number;
    blockedRows: number;
    closureRequests: number;
    gate: "Owner Review";
    runtimeMutation: false;
    noPrCreate: true;
    noMerge: true;
    noTag: true;
    noRemoteWrite: true;
    noDeploy: true;
  };
}

export interface VersioningControlledGitAutomationPlan {
  branch: string;
  baseRef: string;
  commitMessage: string;
  changes: Array<{ path: string; content: string }>;
  prTitle: string;
  prBody: string;
}

export interface MonitoringControlRow {
  id: string;
  label: string;
  status: string;
  evidence: string;
  gate: string;
  tone: VersioningControlTone;
}

export type MonitoringProposalAction = "alert_rule" | "notification_route" | "alert_routine" | "agent_owner" | "remediation_approval";

export interface MonitoringLearningProposalDraft {
  kind: "policy";
  targetKey: string;
  proposedValue: string;
  source: "monitoring-control";
  reason: string;
  evidence: {
    schema: "it-simo.monitoring-control-proposal.v1";
    action: MonitoringProposalAction;
    note: string;
    readyRows: number;
    gatedRows: number;
    blockedRows: number;
    gate: "Owner Review";
    runtimeMutation: false;
    noAlertRuleWrite: true;
    noNotificationSend: true;
    noRunnerAssignment: true;
    noShellAction: true;
    noRestoreAction: true;
    noDeploy: true;
  };
}

export function deriveVersioningControlRows(infra: DashboardData["infra"]): VersioningControlRow[] {
  if (!infra) return [];
  const forgeReady = infra.forgejo.configured && infra.forgejo.reachable && infra.forgejo.status === "ok";
  const mirrorReady = infra.github.configured && infra.github.reachable && infra.forgejo.mirror.remoteName === "github";
  const ciGreen =
    infra.simoCi.configured &&
    infra.simoCi.reachable &&
    infra.simoCi.status === "up" &&
    infra.simoCi.runStatus === "success" &&
    !infra.simoCi.stale &&
    !infra.simoCi.timedOut &&
    infra.simoCi.queueWaiting === 0 &&
    infra.simoCi.queueRunning === 0;
  const headExact = ciGreen && infra.simoCi.exactHeadMatch;
  const openPrs = infra.forgejo.openPullRequests ?? 0;

  return [
    {
      id: "forge-primary",
      label: "Forgejo Primary",
      status: forgeReady ? "readback" : "blockiert",
      detail: `${infra.forgejo.repo} · ${infra.forgejo.defaultBranch}`,
      tone: forgeReady ? "ready" : "blocked",
    },
    {
      id: "github-mirror",
      label: "GitHub Mirror",
      status: mirrorReady ? "sync lesbar" : "pruefen",
      detail: `${infra.forgejo.mirror.remoteName} bleibt mirror-only; Forgejo ist canonical.`,
      tone: mirrorReady ? "ready" : "gated",
    },
    {
      id: "ci-head",
      label: "CI Head",
      status: ciGreen ? "gruen" : "pruefen",
      detail: `${infra.simoCi.branch} · ${infra.simoCi.headSha?.slice(0, 7) ?? "kein HEAD"} · ${infra.simoCi.runStatus}`,
      tone: ciGreen ? "ready" : "gated",
    },
    {
      id: "pr-readiness",
      label: "PR Readiness",
      status: forgeReady && mirrorReady ? "vorbereitbar" : "blockiert",
      detail: `${openPrs} offene PRs; Erstellung bleibt owner-/policy-gated.`,
      tone: forgeReady && mirrorReady ? "gated" : "blocked",
    },
    {
      id: "merge-readiness",
      label: "Merge Readiness",
      status: headExact ? "pruefbar" : "wartet",
      detail: headExact ? "CI ist gruen und exakt am Head; Merge bleibt Owner-Gate." : "CI/Head-Readback reicht noch nicht fuer Merge-Vorschlag.",
      tone: headExact ? "gated" : "blocked",
    },
    {
      id: "safe-tag-readiness",
      label: "Safe-Tag Readiness",
      status: headExact ? "nach Merge" : "wartet",
      detail: "Safe-Tag erst nach Merge-, PR- und CI-Readback; keine Tag-Aktion aus dieser UI.",
      tone: headExact ? "gated" : "blocked",
    },
  ];
}

export function deriveMonitoringControlRows(infra: DashboardData["infra"]): MonitoringControlRow[] {
  if (!infra) return [];
  const monitorSignalOk = infra.kuma.configured && infra.kuma.reachable;
  const alertsHealthy = monitorSignalOk && infra.kuma.downMonitors === 0 && infra.kuma.degradedMonitors === 0 && infra.hostHealth.down === 0;
  const alertsOpen = infra.kuma.downMonitors + infra.kuma.degradedMonitors + infra.hostHealth.down + infra.hostHealth.degraded;
  const backupOwnerAction = infra.backupRestore.restoreRehearsal.ownerActionRequired || infra.backupRestore.blockers.length > 0;
  const ciNeedsAttention = infra.simoCi.status !== "up" || infra.simoCi.stale || infra.simoCi.timedOut || infra.simoCi.queueWaiting > 0 || infra.simoCi.queueRunning > 0;
  const remediationReady = alertsOpen > 0 || backupOwnerAction || ciNeedsAttention;
  const hostHealthy = infra.hostHealth.down === 0 && infra.hostHealth.degraded === 0;
  const backupHealthy = infra.backupRestore.configured && infra.backupRestore.reachable && infra.backupRestore.status === "ok";
  const restoreFresh = infra.backupRestore.restoreRehearsal.status === "fresh";
  const serviceSignals = [
    { configured: infra.orchestrator.configured, reachable: infra.orchestrator.reachable },
    { configured: infra.n8n.configured, reachable: infra.n8n.reachable },
    { configured: infra.forgejo.configured, reachable: infra.forgejo.reachable },
    { configured: infra.simoCi.configured, reachable: infra.simoCi.reachable },
    { configured: infra.kuma.configured, reachable: infra.kuma.reachable },
    { configured: infra.backupRestore.configured, reachable: infra.backupRestore.reachable },
    { configured: true, reachable: hostHealthy },
  ];
  const servicesOnline = serviceSignals.filter((service) => service.configured && service.reachable).length;
  const servicesTotal = serviceSignals.filter((service) => service.configured).length;

  return [
    {
      id: "services",
      label: "Services",
      status: servicesTotal > 0 ? `${servicesOnline}/${servicesTotal} online` : "keine Quelle",
      evidence: `API ${infra.orchestrator.status} · Forge ${infra.forgejo.status} · CI ${infra.simoCi.status} · Kuma ${infra.kuma.status}`,
      gate: "Service-Aenderungen bleiben gesperrt; keine Restart-, Deploy- oder Config-Mutation aus dieser UI.",
      tone: servicesTotal > 0 && servicesOnline === servicesTotal ? "ready" : servicesOnline > 0 ? "gated" : "blocked",
    },
    {
      id: "uptime",
      label: "Uptime",
      status: alertsHealthy && hostHealthy ? "stabil" : "pruefen",
      evidence: `Kuma ${infra.kuma.upMonitors}/${infra.kuma.publicMonitors} public up · Host ${infra.hostHealth.ok}/${infra.hostHealth.total} gesund`,
      gate: "Uptime ist Readback; Statuspage-, Probe- oder Monitor-Write braucht spaeter Owner-Gate.",
      tone: alertsHealthy && hostHealthy ? "ready" : monitorSignalOk ? "gated" : "blocked",
    },
    {
      id: "alert-rules",
      label: "Alert-Regeln",
      status: monitorSignalOk ? "lesbar" : "Quelle fehlt",
      evidence: `Kuma ${infra.kuma.status} · ${infra.kuma.upMonitors}/${infra.kuma.publicMonitors} public up · Host ${infra.hostHealth.ok}/${infra.hostHealth.total} ok`,
      gate: "Regel-Aenderungen bleiben proposal-/owner-gated; keine Alert-Regel-Mutation aus dieser UI.",
      tone: alertsHealthy ? "ready" : monitorSignalOk ? "gated" : "blocked",
    },
    {
      id: "notification-routing",
      label: "Benachrichtigungen",
      status: alertsOpen > 0 || ciNeedsAttention ? "Owner-Hinweis vorbereiten" : "normal",
      evidence: `${alertsOpen} Infra-Alerts · CI ${infra.simoCi.runStatus} · Queue waiting${infra.simoCi.queueWaiting},running${infra.simoCi.queueRunning}`,
      gate: "Notification-Send bleibt gesperrt; nur owner-lesbarer Routing-Vorschlag.",
      tone: alertsOpen > 0 || ciNeedsAttention ? "gated" : "ready",
    },
    {
      id: "backups",
      label: "Backups",
      status: backupHealthy ? "bereit" : infra.backupRestore.reachable ? "conditional" : "Quelle fehlt",
      evidence: `local=${infra.backupRestore.localBackupTimer} · monitor=${infra.backupRestore.backupMonitorTimer} · offsite=${infra.backupRestore.offsiteBackupTimer}`,
      gate: "Backup-Konfiguration und Offsite-/Restic-Aktionen bleiben Owner-/Runbook-gated.",
      tone: backupHealthy ? "ready" : infra.backupRestore.reachable ? "gated" : "blocked",
    },
    {
      id: "restore-status",
      label: "Restore Status",
      status: restoreFresh ? "Proof frisch" : infra.backupRestore.restoreRehearsal.status,
      evidence: `${infra.backupRestore.restoreTestMode} · ${infra.backupRestore.restoreRehearsal.message}`,
      gate: "Live-Restore bleibt owner-only; UI zeigt nur Test-/Proof-Status und startet keinen Restore.",
      tone: restoreFresh ? "ready" : backupOwnerAction ? "gated" : "blocked",
    },
    {
      id: "incidents",
      label: "Incidents",
      status: alertsOpen > 0 || ciNeedsAttention || backupOwnerAction ? "offen/pruefen" : "keine",
      evidence: `${alertsOpen} Infra-Signale · ciNeedsAttention=${String(ciNeedsAttention)} · backupOwnerAction=${String(backupOwnerAction)}`,
      gate: "Incident-Erzeugung, Kundeninfo oder Remediation bleibt proposal-/owner-gated.",
      tone: alertsOpen > 0 || ciNeedsAttention || backupOwnerAction ? "gated" : "ready",
    },
    {
      id: "alert-routines",
      label: "Alert-Routinen",
      status: backupOwnerAction ? "Routine-Pruefung faellig" : "ueberwacht",
      evidence: `Backup ${infra.backupRestore.status} · Restore ${infra.backupRestore.restoreRehearsal.status} · ${infra.backupRestore.blockers.length} Blocker`,
      gate: "Routinen duerfen nur als deaktivierter Vorschlag oder nach Owner-Freigabe geaendert werden.",
      tone: backupOwnerAction ? "gated" : "ready",
    },
    {
      id: "agent-ownership",
      label: "Monitoring Agents",
      status: remediationReady ? "Zuweisung vorschlagen" : "keine Eskalation",
      evidence: `Forge ${infra.forgejo.status} · Host ${infra.hostHealth.status} · Backup ${infra.backupRestore.status}`,
      gate: "Monitoring-Agent-Zustaendigkeit bleibt audit-/capability-gated; keine Runner-Zuweisung aus dieser UI.",
      tone: remediationReady ? "gated" : "ready",
    },
    {
      id: "remediation-approval",
      label: "Remediation-Approval",
      status: remediationReady ? "Owner-Gate erforderlich" : "kein Fix offen",
      evidence: remediationReady
        ? "Mindestens ein Monitoring-Signal braucht Entscheidung, Proof oder manuelle Remediation."
        : "Monitoring-Signale liefern keinen aktuellen Remediation-Bedarf.",
      gate: "Keine SSH-, Restart-, Restore-, Deploy- oder Shell-Aktion aus dieser UI.",
      tone: remediationReady ? "gated" : "ready",
    },
  ];
}

export function buildMonitoringLearningProposal(
  action: MonitoringProposalAction,
  note: string,
  rows: MonitoringControlRow[],
): MonitoringLearningProposalDraft {
  const normalizedNote = note.trim();
  const readyRows = rows.filter((row) => row.tone === "ready").length;
  const gatedRows = rows.filter((row) => row.tone === "gated").length;
  const blockedRows = rows.filter((row) => row.tone === "blocked").length;
  const actionLabel: Record<MonitoringProposalAction, string> = {
    alert_rule: "Alert-Regel vorschlagen",
    notification_route: "Benachrichtigung vorschlagen",
    alert_routine: "Alert-Routine vorschlagen",
    agent_owner: "Monitoring-Agent-Zustaendigkeit vorschlagen",
    remediation_approval: "Remediation-Freigabe vorbereiten",
  };
  return {
    kind: "policy",
    targetKey: `monitoring_control.policy.${action}`,
    proposedValue: [
      "Monitoring Control Proposal",
      `Aktion: ${actionLabel[action]}`,
      `Readback: ${readyRows}/${rows.length} ready, ${gatedRows} gated, ${blockedRows} blocked`,
      "",
      "Owner-Notiz:",
      normalizedNote,
      "",
      "Gate: Owner Review",
      "Live-Aktion: nein",
    ].join("\n"),
    source: "monitoring-control",
    reason: `Owner-visible monitoring control proposal for ${action}.`,
    evidence: {
      schema: "it-simo.monitoring-control-proposal.v1",
      action,
      note: normalizedNote,
      readyRows,
      gatedRows,
      blockedRows,
      gate: "Owner Review",
      runtimeMutation: false,
      noAlertRuleWrite: true,
      noNotificationSend: true,
      noRunnerAssignment: true,
      noShellAction: true,
      noRestoreAction: true,
      noDeploy: true,
    },
  };
}

export function deriveVersioningClosureRequestRows(infra: DashboardData["infra"]): VersioningClosureRequestRow[] {
  if (!infra) return [];
  const rows = deriveVersioningControlRows(infra);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const forgeReady = byId.get("forge-primary")?.tone === "ready";
  const mirrorReady = byId.get("github-mirror")?.tone === "ready";
  const ciReady = byId.get("ci-head")?.tone === "ready";
  const mergeReady = byId.get("merge-readiness")?.tone === "gated";
  const tagReady = byId.get("safe-tag-readiness")?.tone === "gated";
  const openPrs = infra.forgejo.openPullRequests ?? 0;

  return [
    {
      id: "request-pr",
      label: "PR-Anfrage",
      request: forgeReady && mirrorReady ? "bereit zur Owner-Freigabe" : "Remote-Readback fehlt",
      evidence: `Forgejo primary ${forgeReady ? "ok" : "offen"} · GitHub mirror ${mirrorReady ? "ok" : "offen"} · ${openPrs} offene PRs`,
      gate: "Owner/Admin + PR-Policy; keine PR-Erstellung aus dieser UI.",
      tone: forgeReady && mirrorReady ? "gated" : "blocked",
    },
    {
      id: "request-merge",
      label: "Merge-Anfrage",
      request: mergeReady ? "nach PR-Review anfragbar" : "CI/Head zuerst pruefen",
      evidence: ciReady
        ? `CI #${infra.simoCi.runNumber ?? "-"} ${infra.simoCi.runStatus} exakt am Head ${infra.simoCi.headSha?.slice(0, 7) ?? "-"}`
        : `CI ${infra.simoCi.runStatus} · exactHead=${String(infra.simoCi.exactHeadMatch)} · stale=${String(infra.simoCi.stale)}`,
      gate: "Owner-Gate + PR-Readback + CI-Readback; kein Merge-Start aus dieser UI.",
      tone: mergeReady ? "gated" : "blocked",
    },
    {
      id: "request-safe-tag",
      label: "Safe-Tag-Anfrage",
      request: tagReady ? "erst nach Merge-Readback" : "wartet auf Merge-/CI-Kette",
      evidence: tagReady ? "Safe-Tag braucht Merge-SHA, PR-Link, CI gruen und Remote-Tag-Readback." : "Safe-Tag bleibt blockiert, bis PR/Merge/CI voll belegt sind.",
      gate: "Owner-Gate + Safe-Tag-Protokoll; keine Tag-Aktion aus dieser UI.",
      tone: tagReady ? "gated" : "blocked",
    },
  ];
}

export function buildVersioningLearningProposal(
  action: VersioningProposalAction,
  note: string,
  rows: VersioningControlRow[],
  closureRows: VersioningClosureRequestRow[],
): VersioningLearningProposalDraft {
  const normalizedNote = note.trim();
  const readyRows = rows.filter((row) => row.tone === "ready").length;
  const gatedRows = rows.filter((row) => row.tone === "gated").length;
  const blockedRows = rows.filter((row) => row.tone === "blocked").length;
  const actionLabel: Record<VersioningProposalAction, string> = {
    prepare_pr: "PR vorbereiten",
    request_merge: "Merge anfragen",
    request_safe_tag: "Safe Tag anfragen",
    check_mirror_drift: "Mirror Drift pruefen",
    release_readiness: "Release Readiness pruefen",
  };
  return {
    kind: "policy",
    targetKey: `forge_versioning.control.${action}`,
    proposedValue: [
      "Forge / Versioning Proposal",
      `Aktion: ${actionLabel[action]}`,
      `Readback: ${readyRows}/${rows.length} ready, ${gatedRows} gated, ${blockedRows} blocked`,
      `Closure Requests: ${closureRows.length}`,
      "",
      "Owner-Notiz:",
      normalizedNote,
      "",
      "Gate: Owner Review",
      "Remote Write: nein",
    ].join("\n"),
    source: "forge-versioning-control",
    reason: `Owner-visible Forge/Versioning proposal for ${action}.`,
    evidence: {
      schema: "it-simo.forge-versioning-proposal.v1",
      action,
      note: normalizedNote,
      readyRows,
      gatedRows,
      blockedRows,
      closureRequests: closureRows.length,
      gate: "Owner Review",
      runtimeMutation: false,
      noPrCreate: true,
      noMerge: true,
      noTag: true,
      noRemoteWrite: true,
      noDeploy: true,
    },
  };
}

export function buildVersioningControlledGitAutomationPlan(
  note: string,
  rows: VersioningControlRow[],
  closureRows: VersioningClosureRequestRow[],
  now = new Date(),
): VersioningControlledGitAutomationPlan {
  const day = now.toISOString().slice(0, 10);
  const compactDay = day.replace(/-/g, "");
  const readyRows = rows.filter((row) => row.tone === "ready").length;
  const gatedRows = rows.filter((row) => row.tone === "gated").length;
  const blockedRows = rows.filter((row) => row.tone === "blocked").length;
  const evidencePath = `docs/abnahme-protokolle/SIMO-FORGE-VERSIONING-CONTROLLED-PR-PLAN-${compactDay}.md`;
  const content = [
    `# SIMO-FORGE-VERSIONING-CONTROLLED-PR-PLAN-${compactDay}`,
    "",
    "Status: **CONTROLLED PR PLAN REQUEST / NO DIRECT REMOTE ACTION**",
    "",
    `Datum: ${day}`,
    "",
    "## Ziel",
    "",
    "Forge-/Versioning-Closure aus der Full-Control-Flaeche als kontrollierten",
    "PR-Plan anlegen, ohne PR, Merge, Safe-Tag, Deploy oder Remote-Write direkt",
    "aus der UI zu starten.",
    "",
    "## Readback",
    "",
    `- Ready rows: ${readyRows}/${rows.length}`,
    `- Gated rows: ${gatedRows}`,
    `- Blocked rows: ${blockedRows}`,
    `- Closure requests: ${closureRows.length}`,
    "",
    "## Boundary",
    "",
    "- Branch-/Commit-/PR-Plan nur nach separater Owner Approval Execution.",
    "- Kein Push aus dieser UI.",
    "- Keine direkte PR-Erstellung aus dieser UI.",
    "- Kein Merge aus dieser UI.",
    "- Kein Safe-Tag aus dieser UI.",
    "- Kein Deploy aus dieser UI.",
    "- Owner-Notiz bleibt im Approval-/PR-Text und darf keine Secret-Werte enthalten.",
    "",
    "## Final-Acceptance-Wahrheit",
    "",
    "Dieser Slice reduziert `final-direct-editors`, schliesst aber weder",
    "`full-governed-real-execution` noch `release:final-acceptance:current`.",
    "",
  ].join("\n");
  return {
    branch: `work/simo-forge-versioning-controlled-pr-plan-${compactDay}`,
    baseRef: "feat/simo-personal-assistant-stability-1",
    commitMessage: "docs: record forge versioning controlled PR plan",
    changes: [{ path: evidencePath, content }],
    prTitle: "SIMO Forge versioning controlled PR plan",
    prBody: [
      "## Ziel",
      "",
      "Owner-gated Forge-/Versioning-Closure als controlled git automation PR-Plan vorbereiten.",
      "",
      "## Owner-Notiz",
      "",
      note.trim(),
      "",
      "## Boundary",
      "",
      "- Plan/Approval only from UI",
      "- no push",
      "- no direct PR create",
      "- no merge",
      "- no safe tag",
      "- no deploy",
    ].join("\n"),
  };
}

function versioningProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Forge-/Versioning-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|policy|versioning|forge/i.test(text)) {
    return "Forge-/Versioning-Vorschlag braucht Aktion und Owner-Notiz.";
  }
  return ownerFacingGlobalError(text);
}

function VersioningProposalForm({
  rows,
  closureRows,
  onCreated,
}: {
  rows: VersioningControlRow[];
  closureRows: VersioningClosureRequestRow[];
  onCreated: () => void;
}): JSX.Element {
  const [action, setAction] = useState<VersioningProposalAction>("prepare_pr");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [executeBusy, setExecuteBusy] = useState(false);
  const [controlledPlan, setControlledPlan] = useState<{ approvalId: string; requestId: string; plan: VersioningControlledGitAutomationPlan } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = note.trim().length > 0 && !busy && !planBusy && !executeBusy;
  const canPlan = note.trim().length > 0 && !busy && !planBusy && !executeBusy;
  const canExecuteApprovedPlan = controlledPlan !== null && !busy && !planBusy && !executeBusy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const draft = buildVersioningLearningProposal(action, note, rows, closureRows);
      const result = await api.createLearningProposal(draft);
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setNote("");
      setNotice("Forge-/Versioning-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(versioningProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  const planControlledPr = async (): Promise<void> => {
    if (!canPlan) return;
    setPlanBusy(true);
    setError(null);
    setNotice(null);
    try {
      const plan = buildVersioningControlledGitAutomationPlan(note, rows, closureRows);
      const result = await api.planControlledGitAutomation(plan);
      if (!result.ok) throw new Error("controlled_git_automation_plan_failed");
      setControlledPlan({ approvalId: result.approval.id, requestId: result.requestId, plan });
      setNote("");
      setNotice(`Controlled PR-Plan wartet auf Owner Approval: ${result.approval.id}`);
      onCreated();
    } catch (planError) {
      setError(versioningProposalErrorCopy(planError));
    } finally {
      setPlanBusy(false);
    }
  };

  const executeApprovedPlan = async (): Promise<void> => {
    if (!controlledPlan) return;
    setExecuteBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.executeApprovedControlledGitAutomation({
        ...controlledPlan.plan,
        approvalId: controlledPlan.approvalId,
      });
      if (!result.ok) throw new Error(result.reason ?? "controlled_git_approved_execute_blocked");
      setNotice(`Approved PR-Plan lokal vorbereitet: ${result.result.branch} @ ${result.result.commit.slice(0, 7)}`);
      setControlledPlan(null);
      onCreated();
    } catch (executeError) {
      setError(versioningProposalErrorCopy(executeError));
    } finally {
      setExecuteBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-jarvis-border/45 bg-black/10 p-2">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="font-medium uppercase tracking-[0.12em] text-jarvis-muted">Versioning Proposal</span>
        <span className="shrink-0 text-jarvis-muted">Review-only</span>
      </div>
      <select
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-jarvis-border bg-jarvis-bg/70 px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={action}
        aria-label="Versioning Proposal Aktion"
        onChange={(event) => setAction(event.currentTarget.value as VersioningProposalAction)}
      >
        <option value="prepare_pr">PR vorbereiten</option>
        <option value="request_merge">Merge anfragen</option>
        <option value="request_safe_tag">Safe Tag anfragen</option>
        <option value="check_mirror_drift">Mirror Drift pruefen</option>
        <option value="release_readiness">Release Readiness pruefen</option>
      </select>
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-jarvis-border bg-jarvis-bg/70 px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={note}
        maxLength={5000}
        aria-label="Versioning Proposal Notiz"
        placeholder="PR-, Merge-, Safe-Tag-, Mirror- oder Release-Readiness-Anfrage als Review-Text"
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 min-h-10 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Versioning-Vorschlag in Review Queue legen
      </button>
      <button
        type="button"
        disabled={!canPlan}
        className="mt-1 min-h-10 w-full rounded border border-sky-300/25 bg-sky-300/[0.08] px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] text-sky-100 hover:border-sky-200/60 disabled:opacity-50"
        onClick={() => void planControlledPr()}
      >
        Controlled PR-Plan anlegen
      </button>
      <button
        type="button"
        disabled={!canExecuteApprovedPlan}
        className="mt-1 min-h-10 w-full rounded border border-emerald-300/25 bg-emerald-300/[0.08] px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] text-emerald-100 hover:border-emerald-200/60 disabled:opacity-50"
        onClick={() => void executeApprovedPlan()}
      >
        Approved PR-Plan lokal vorbereiten
      </button>
      <p className="mt-1 text-[10px] leading-relaxed text-jarvis-muted">
        Proposal speichert Review-Text. Controlled PR-Plan erzeugt Approval, Audit und Evidence; Approved Execute erstellt nur lokalen Worktree+Commit. Kein Push, keine direkte PR-Erstellung, kein Merge, kein Tag, kein Deploy.
      </p>
      {controlledPlan && (
        <div className="mt-1 rounded border border-emerald-300/20 bg-emerald-300/[0.05] px-2 py-1 text-[10px] text-emerald-100">
          Approval: <span className="font-mono">{controlledPlan.approvalId}</span> · Request: <span className="font-mono">{controlledPlan.requestId}</span>
        </div>
      )}
      {notice && <div className="mt-1 break-words text-[10px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[10px] text-rose-200">{error}</div>}
    </div>
  );
}

function monitoringProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Monitoring-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|policy|monitoring/i.test(text)) {
    return "Monitoring-Vorschlag braucht Aktion und Owner-Notiz.";
  }
  return ownerFacingGlobalError(text);
}

function MonitoringProposalForm({
  rows,
  onCreated,
}: {
  rows: MonitoringControlRow[];
  onCreated: () => void;
}): JSX.Element {
  const [action, setAction] = useState<MonitoringProposalAction>("alert_rule");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = note.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const draft = buildMonitoringLearningProposal(action, note, rows);
      const result = await api.createLearningProposal(draft);
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setNote("");
      setNotice("Monitoring-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(monitoringProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-jarvis-border/45 bg-black/10 p-2">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="font-medium uppercase tracking-[0.12em] text-jarvis-muted">Monitoring Proposal</span>
        <span className="shrink-0 text-jarvis-muted">Review-only</span>
      </div>
      <select
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-jarvis-border bg-jarvis-bg/70 px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={action}
        aria-label="Monitoring Proposal Aktion"
        onChange={(event) => setAction(event.currentTarget.value as MonitoringProposalAction)}
      >
        <option value="alert_rule">Alert-Regel vorschlagen</option>
        <option value="notification_route">Benachrichtigung vorschlagen</option>
        <option value="alert_routine">Alert-Routine vorschlagen</option>
        <option value="agent_owner">Monitoring-Agent-Zustaendigkeit vorschlagen</option>
        <option value="remediation_approval">Remediation-Freigabe vorbereiten</option>
      </select>
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-jarvis-border bg-jarvis-bg/70 px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={note}
        maxLength={5000}
        aria-label="Monitoring Proposal Notiz"
        placeholder="Alert-Regel, Benachrichtigung, Routine, Agent-Zustaendigkeit oder Remediation als Review-Text"
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 min-h-10 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[10px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Monitoring-Vorschlag in Review Queue legen
      </button>
      <p className="mt-1 text-[10px] leading-relaxed text-jarvis-muted">
        Speichert nur ein policy Learning Proposal. Keine Alert-Regel-Mutation, kein Notification-Send, keine Runner-Zuweisung, keine Shell, kein Restore, kein Deploy.
      </p>
      {notice && <div className="mt-1 break-words text-[10px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[10px] text-rose-200">{error}</div>}
    </div>
  );
}

export function InfraPanel({ data }: { data: DashboardData }): JSX.Element {
  const inf = data.infra;
  const ghReachable = inf?.github.reachable ?? false;
  const ghConfigured = inf?.github.configured ?? false;
  const versioningRows = deriveVersioningControlRows(inf);
  const closureRequestRows = deriveVersioningClosureRequestRows(inf);
  const monitoringRows = deriveMonitoringControlRows(inf);
  return (
    <Panel title="Infrastruktur" accent="simo-accent-neutral">
      {!inf ? (
        <div className="text-[11px] text-jarvis-muted">Probe läuft…</div>
      ) : (
        <div className="space-y-3">
          <ul className="space-y-1">
            <ProbeRow p={inf.orchestrator} />
            <ProbeRow p={inf.n8n} />
            <li className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${FORGEJO_ACCENT[inf.forgejo.status] ?? "simo-accent-neutral"}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{inf.forgejo.name} Primary</span>
                <span className="block truncate text-[10px] text-jarvis-muted">{inf.forgejo.defaultBranch}</span>
              </span>
              <span className="text-[10px] text-jarvis-muted shrink-0">{FORGEJO_LABEL[inf.forgejo.status] ?? inf.forgejo.status}</span>
            </li>
            <li className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${SIMO_CI_ACCENT[inf.simoCi.status] ?? "simo-accent-neutral"}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{inf.simoCi.name}</span>
                <span className="block truncate text-[10px] text-jarvis-muted">
                  {inf.simoCi.branch} · {inf.simoCi.headSha?.slice(0, 7) ?? "kein HEAD"} · Queue waiting{inf.simoCi.queueWaiting},running{inf.simoCi.queueRunning}
                </span>
              </span>
              <span className="text-[10px] text-jarvis-muted shrink-0">
                {inf.simoCi.timedOut ? "Timeout" : inf.simoCi.stale ? "stale" : inf.simoCi.runNumber == null ? SIMO_CI_LABEL[inf.simoCi.status] ?? inf.simoCi.status : `#${inf.simoCi.runNumber} ${inf.simoCi.runStatus}`}
              </span>
            </li>
            <li className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${KUMA_ACCENT[inf.kuma.status] ?? "simo-accent-neutral"}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{inf.kuma.name}</span>
                <span className="block truncate text-[10px] text-jarvis-muted">
                  {inf.kuma.publicMonitors > 0 ? `${inf.kuma.upMonitors}/${inf.kuma.publicMonitors} public up` : "UI reachable"}
                </span>
              </span>
              <span className="text-[10px] text-jarvis-muted shrink-0">{KUMA_LABEL[inf.kuma.status] ?? inf.kuma.status}</span>
            </li>
            <BackupRestoreRow signal={inf.backupRestore} />
            <HostHealthRow signal={inf.hostHealth} />
            <li className="flex items-center gap-2 text-xs">
              <span className={`status-dot ${ghReachable ? "simo-accent-ok" : ghConfigured ? "simo-accent-warn" : "simo-accent-neutral"}`} />
              <span className="truncate flex-1 min-w-0">{inf.github.name} Mirror</span>
              <span className="text-[10px] text-jarvis-muted shrink-0">
                {ghReachable ? "sync" : ghConfigured ? "degradiert" : "nicht verbunden"}
              </span>
            </li>
          </ul>
          {versioningRows.length > 0 && (
            <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-2">
              <div className="mb-2 flex items-center justify-between gap-2 text-[11px]">
                <span className="font-medium text-jarvis-text">Forge / Versioning</span>
                <span className="shrink-0 text-[10px] text-jarvis-muted">proposal-only</span>
              </div>
              <div className="grid gap-1.5 md:grid-cols-2">
                {versioningRows.map((row) => (
                  <div
                    key={row.id}
                    className="min-w-0 rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 px-2 py-1.5"
                    data-versioning-tone={row.tone}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`status-dot ${row.tone === "ready" ? "simo-accent-ok" : row.tone === "gated" ? "simo-accent-confirming" : "simo-accent-error"}`} />
                      <span className="min-w-0 flex-1 truncate text-xs text-jarvis-text" title={row.label}>{row.label}</span>
                      <span className="shrink-0 text-[10px] text-jarvis-muted">{row.status}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-[10px] text-jarvis-muted" title={row.detail}>{row.detail}</div>
                  </div>
                ))}
              </div>
              {closureRequestRows.length > 0 && (
                <div className="mt-2 rounded-md border border-jarvis-border/45 bg-black/10 p-2">
                  <div className="mb-1.5 flex items-center justify-between gap-2 text-[10px]">
                    <span className="font-medium text-jarvis-text">Closure-Anfragen</span>
                    <span className="shrink-0 text-jarvis-muted">owner-gated</span>
                  </div>
                  <div className="grid gap-1.5">
                    {closureRequestRows.map((row) => (
                      <div key={row.id} className="min-w-0 rounded-md border border-jarvis-border/40 bg-jarvis-bg/35 px-2 py-1.5" data-versioning-request-tone={row.tone}>
                        <div className="flex min-w-0 items-center gap-2">
                          <span className={`status-dot ${row.tone === "ready" ? "simo-accent-ok" : row.tone === "gated" ? "simo-accent-confirming" : "simo-accent-error"}`} />
                          <span className="min-w-0 flex-1 truncate text-[11px] text-jarvis-text" title={row.label}>{row.label}</span>
                          <span className="shrink-0 text-[10px] text-jarvis-muted">{row.request}</span>
                        </div>
                        <div className="mt-1 line-clamp-2 text-[10px] text-jarvis-muted" title={row.evidence}>{row.evidence}</div>
                        <div className="mt-1 line-clamp-2 text-[10px] text-amber-100/80" title={row.gate}>{row.gate}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <VersioningProposalForm rows={versioningRows} closureRows={closureRequestRows} onCreated={data.refresh} />
            </div>
          )}
          {monitoringRows.length > 0 && (
            <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-2">
              <div className="mb-2 flex items-center justify-between gap-2 text-[11px]">
                <span className="font-medium text-jarvis-text">Monitoring Control</span>
                <span className="shrink-0 text-[10px] text-jarvis-muted">owner-gated</span>
              </div>
              <div className="grid gap-1.5 md:grid-cols-2">
                {monitoringRows.map((row) => (
                  <div key={row.id} className="min-w-0 rounded-md border border-jarvis-border/45 bg-jarvis-surface/35 px-2 py-1.5" data-monitoring-control-tone={row.tone}>
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`status-dot ${row.tone === "ready" ? "simo-accent-ok" : row.tone === "gated" ? "simo-accent-confirming" : "simo-accent-error"}`} />
                      <span className="min-w-0 flex-1 truncate text-xs text-jarvis-text" title={row.label}>{row.label}</span>
                      <span className="shrink-0 text-[10px] text-jarvis-muted">{row.status}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-[10px] text-jarvis-muted" title={row.evidence}>{row.evidence}</div>
                    <div className="mt-1 line-clamp-2 text-[10px] text-amber-100/80" title={row.gate}>{row.gate}</div>
                  </div>
                ))}
              </div>
              <MonitoringProposalForm rows={monitoringRows} onCreated={data.refresh} />
            </div>
          )}
          <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/30 p-2">
            <div className="mb-2 flex items-center justify-between gap-2 text-[11px]">
              <span className="font-medium text-jarvis-text">Tailnet Inventory</span>
              <span className="shrink-0 font-mono text-[10px] text-jarvis-muted">
                {inf.tailnet.tailscaleHosts}/{inf.tailnet.total} · {inf.tailnet.privateNetworkHosts} privat
              </span>
            </div>
            <ul className="space-y-2">
              {inf.tailnet.hosts.map((host) => <TailnetHostRow key={host.id} host={host} />)}
            </ul>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function RuntimeReliabilityPanel({ data }: { data: DashboardData }): JSX.Element {
  const baseline = data.runtimeReliability;
  const degraded = baseline?.signals.some((s) => s.status === "degraded" || s.status === "down") ?? false;
  const notMeasured = baseline?.signals.filter((s) => !s.measured).length ?? 0;
  return (
    <Panel
      title="Runtime Reliability"
      accent={degraded ? "simo-accent-warn" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{baseline ? `${baseline.window.recentRuns}/${baseline.window.recentTools}` : "-"}</span>}
    >
      {!baseline ? (
        <div className="text-[11px] text-jarvis-muted">Baseline wird gelesen...</div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            <div className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1">
              <div className="kpi text-sm">{baseline.modelLatency.averageMs == null ? "-" : `${baseline.modelLatency.averageMs} ms`}</div>
              <div className="text-[9px] uppercase tracking-wider text-jarvis-muted">Latenz</div>
            </div>
            <div className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1">
              <div className="kpi text-sm">{Math.round(baseline.errorRate.runs.rate * 100)}%</div>
              <div className="text-[9px] uppercase tracking-wider text-jarvis-muted">Run Fehler</div>
            </div>
            <div className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1">
              <div className="kpi text-sm">{baseline.timeoutSignals.toolTimeouts + baseline.timeoutSignals.ollamaTimeouts}</div>
              <div className="text-[9px] uppercase tracking-wider text-jarvis-muted">Timeouts</div>
            </div>
          </div>
          <ul className="space-y-1 max-h-52 overflow-y-auto simo-thin-scroll pr-1">
            {baseline.signals.map((signal) => (
              <li key={signal.id} className="flex items-center gap-2 text-xs">
                <span className={`status-dot ${RELIABILITY_ACCENT[signal.status] ?? "simo-accent-neutral"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate" title={signal.detail}>{signal.label}</span>
                  <span className="block truncate text-[10px] text-jarvis-muted">{signal.value}</span>
                </span>
                <span className="text-[10px] text-jarvis-muted shrink-0">{RELIABILITY_LABEL[signal.status] ?? signal.status}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-1 text-[10px] text-jarvis-muted">
            <span className="badge bg-jarvis-bg/60">read-only</span>
            <span className="badge bg-jarvis-bg/60">no shell/git</span>
            {notMeasured > 0 && <span className="badge bg-jarvis-bg/60">{notMeasured} Quellen offen</span>}
          </div>
        </div>
      )}
    </Panel>
  );
}
